import { useCallback, useState } from "react";
import { QueryObserverResult, useQueryClient } from "@tanstack/react-query";
import { keyBy } from "@common/helpers/collections";
import { pluralizeWithCount } from "@common/helpers/string";
import { ImportedAccount } from "@common/schemas/domains/accounting/importedAccount.schema";
import {
    Account,
    GetAccountsResponse,
} from "@common/types/domains/accounting/account";
import { getWorkspaceAccountsKey } from "@api/account.api";
import { accountingReportsQueryKeys } from "@api/accountingReports.api";
import {
    ImportedAccountsData,
    useBulkUpdateImportedAccounts,
} from "@api/accountingIntegration.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { getAccountsQueryKey } from "@hooks/useAccounts";
import { getMapToAccountUpdate } from "../accountMappingBulkEligibility";
import {
    findMappingsBrokenByChartEdits,
    MappingSnapshotEntry,
    snapshotEnabledMappings,
} from "./findMappingsBrokenByChartEdits";

interface Params {
    workspaceId: string;
    entityId: number;
    importedAccounts: ImportedAccount[] | undefined;
    kickAccountsById: Record<string, Account>;
    refetchImportedAccounts: () => Promise<
        QueryObserverResult<ImportedAccountsData>
    >;
}

// Owns one editor session from the mapping step: the snapshot of enabled
// mappings taken on open is what the post-close refresh compares against. It
// is state, not a ref: the refresh closes over its own session's snapshot, so
// a reopen during a still-running refresh cannot swap it.
export function useChartEditorSession({
    workspaceId,
    entityId,
    importedAccounts,
    kickAccountsById,
    refetchImportedAccounts,
}: Params) {
    const queryClient = useQueryClient();
    const toaster = useToaster();
    const { mutateAsync: bulkUpdate } = useBulkUpdateImportedAccounts(
        workspaceId,
        entityId,
    );
    const [mappedBefore, setMappedBefore] = useState<MappingSnapshotEntry[]>(
        [],
    );
    const [isEditorOpen, setIsEditorOpen] = useState(false);

    const openEditor = useCallback(() => {
        setMappedBefore(
            snapshotEnabledMappings({
                importedAccounts: importedAccounts ?? [],
                kickAccountsById,
            }),
        );
        setIsEditorOpen(true);
    }, [importedAccounts, kickAccountsById]);
    const closeEditor = useCallback(() => setIsEditorOpen(false), []);

    const refreshAfterChartEdits = useCallback(async () => {
        const accountsQueryKey = getAccountsQueryKey(entityId);
        const [, , , refetched] = await Promise.all([
            queryClient.invalidateQueries({
                queryKey: [accountingReportsQueryKeys.chartOfAccounts],
            }),
            queryClient.invalidateQueries({ queryKey: accountsQueryKey }),
            queryClient.invalidateQueries({
                queryKey: getWorkspaceAccountsKey(workspaceId),
            }),
            refetchImportedAccounts(),
        ]);
        if (refetched.isError || refetched.data === undefined) {
            throw (
                refetched.error ?? new Error("Imported accounts refetch failed")
            );
        }
        const accountsQueryState =
            queryClient.getQueryState<GetAccountsResponse>(accountsQueryKey);
        if (accountsQueryState?.error) {
            throw accountsQueryState.error;
        }
        const kickAccounts = accountsQueryState?.data?.accounts ?? [];
        const { deletedImportedAccountIds, archivedImportedAccountIds } =
            findMappingsBrokenByChartEdits({
                mappedBefore,
                importedAccounts: refetched.data.accounts,
                kickAccountsById: keyBy(kickAccounts, (account) => account.id),
            });
        if (archivedImportedAccountIds.length > 0) {
            await bulkUpdate({
                updates: archivedImportedAccountIds.map((id) =>
                    getMapToAccountUpdate({ id, accountId: null }),
                ),
            });
        }
        const clearedCount =
            deletedImportedAccountIds.length +
            archivedImportedAccountIds.length;
        if (clearedCount > 0) {
            toaster.warning(
                `${pluralizeWithCount("mapping", clearedCount)} cleared: the Kick accounts are deleted or archived`,
            );
        }
    }, [
        bulkUpdate,
        entityId,
        mappedBefore,
        queryClient,
        refetchImportedAccounts,
        toaster,
        workspaceId,
    ]);

    return { isEditorOpen, openEditor, closeEditor, refreshAfterChartEdits };
}
